import { useCallback, useEffect, useState } from 'react';
import { cmsGraphql } from '@xolv-mybrightlink/common';
import { useFetchSavedArticleSlugs } from './useFetchSavedArticleSlugs';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
import { gql } from '@apollo/client';
import _ from 'lodash';
import { Article, ArticleResponse } from '../models/ArticleResult';

const GET_ARTICLE_LIST_BY_SLUGS = gql`
  query Posts($slugList: [String]) {
    posts(where: { nameIn: $slugList }) {
      nodes {
        id
        date
        slug
        title(format: RENDERED)
        content(format: RENDERED)
        databaseId
        categories {
          nodes {
            name
            databaseId
          }
        }
        featuredImage {
          node {
            sourceUrl
          }
        }
      }
      pageInfo {
        endCursor
      }
    }
  }
`;

type SavedArticleFetchResult =
  | { type: 'LOADING' }
  | { type: 'ERROR' }
  | {
      type: 'DATA';
      articles: Article[];
      anchorGuid: string | null;
      hasLoadMore: boolean;
    };

export const useFetchSavedArticles = (
  anchorGuid: string | null,
  pageSize?: number,
  retry?: boolean,
) => {
  const [savedArticleFetchResult, setSavedArticleFetchResult] =
    useState<SavedArticleFetchResult>({ type: 'LOADING' });
  const getSavedSlugs = useFetchSavedArticleSlugs();
  const intl = useIntl();

  const getArticleListBySlugs = useCallback(
    async (slugs: string[]) => {
      try {
        const result = await cmsGraphql.query<ArticleResponse>({
          query: GET_ARTICLE_LIST_BY_SLUGS,
          variables: { slugList: slugs },
        });
        const cloneArticles = _.cloneDeep(result.data.posts.nodes);
        cloneArticles.forEach((item) => (item.isSaved = true));
        return cloneArticles;
      } catch {
        toast(intl.formatMessage({ id: 'common.error.message' }), {
          toastId: 'common.error.message',
        });
      }
    },
    [intl],
  );

  useEffect(() => {
    const fetch = async () => {
      try {
        setSavedArticleFetchResult({ type: 'LOADING' });
        const savedSlugs = await getSavedSlugs(anchorGuid, pageSize ?? 10);
        if (savedSlugs.bookmarks.length === 0) {
          setSavedArticleFetchResult({
            type: 'DATA',
            articles: [],
            hasLoadMore: false,
            anchorGuid: null,
          });
          return;
        }
        const savedArticles = await getArticleListBySlugs(
          savedSlugs.bookmarks.map((bookmark) => bookmark.slug),
        );
        setSavedArticleFetchResult({
          type: 'DATA',
          articles: savedArticles ? savedArticles : [],
          hasLoadMore: savedArticles ? savedSlugs.hasLoadMore : false,
          anchorGuid:
            savedSlugs.bookmarks[savedSlugs.bookmarks.length - 1].guid,
        });
      } catch (e) {
        toast(intl.formatMessage({ id: 'common.error.message' }), {
          toastId: 'common.error.message',
        });
        setSavedArticleFetchResult({ type: 'ERROR' });
      }
    };
    fetch();
  }, [anchorGuid, getArticleListBySlugs, getSavedSlugs, intl, pageSize, retry]);

  return savedArticleFetchResult;
};
